import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { getApiData } from "../../../../utils/APIHelper";
import ProjectListIssues from "./components/ProjectListIssues";
import ProjectCardIssues from "./components/ProjectCardIssues";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ISSUE_STATUS, ISSUE_SOURCE_TYPE, formatEnumForDisplay } from "./components/constants";

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: ISSUE_STATUS.OPEN, label: formatEnumForDisplay(ISSUE_STATUS.OPEN) },
  { value: ISSUE_STATUS.IN_PROGRESS, label: formatEnumForDisplay(ISSUE_STATUS.IN_PROGRESS) },
  { value: ISSUE_STATUS.DONE, label: formatEnumForDisplay(ISSUE_STATUS.DONE) },
  { value: ISSUE_STATUS.RESOLVED, label: formatEnumForDisplay(ISSUE_STATUS.RESOLVED) },
];

const SOURCE_TYPE_OPTIONS = [
  { value: "", label: "All Source Types" },
  { value: ISSUE_SOURCE_TYPE.DAILY_UPDATE, label: formatEnumForDisplay(ISSUE_SOURCE_TYPE.DAILY_UPDATE) },
  { value: ISSUE_SOURCE_TYPE.INSPECTION, label: formatEnumForDisplay(ISSUE_SOURCE_TYPE.INSPECTION) },
  { value: ISSUE_SOURCE_TYPE.SITE_VISIT, label: formatEnumForDisplay(ISSUE_SOURCE_TYPE.SITE_VISIT) },
  { value: ISSUE_SOURCE_TYPE.CUSTOMER, label: formatEnumForDisplay(ISSUE_SOURCE_TYPE.CUSTOMER) },
];

const PAGE_SIZE = 20;

export default function IssuesSection({ projectId, onLogIssueClick, onIssueClick, refetchRef }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("size", String(PAGE_SIZE));
      if (statusFilter) params.append("status", statusFilter);
      if (sourceTypeFilter) params.append("sourceType", sourceTypeFilter);
      const response = await getApiData(
        `api/v2/issue-logs/project/${projectId}?${params.toString()}`,
        "GET",
        null,
        null,
        true
      );
      const raw = response?.data ?? response;
      const content = raw?.content ?? [];
      const totalPages = raw?.totalPages ?? 1;
      const totalElements = raw?.totalElements ?? 0;
      setData({ content, totalPages, totalElements });
    } catch (err) {
      console.error(err);
      setData({ content: [], totalPages: 1, totalElements: 0 });
    } finally {
      setLoading(false);
    }
  }, [projectId, page, statusFilter, sourceTypeFilter]);

  useEffect(() => {
    if (refetchRef) refetchRef.current = fetchList;
  }, [refetchRef, fetchList]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const list = data?.content ?? [];
  const totalPages = data?.totalPages ?? 1;

  if (!projectId) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "0 16px" }}>
        <p style={{ fontWeight: 500, fontFamily: "Inter Tight", margin: 0 }}>Issues</p>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="issues-status-label">Status</InputLabel>
            <Select
              labelId="issues-status-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: "12px", fontSize: 14 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="issues-source-type-label">Source Type</InputLabel>
            <Select
              labelId="issues-source-type-label"
              value={sourceTypeFilter}
              label="Source Type"
              onChange={(e) => {
                setSourceTypeFilter(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: "12px", fontSize: 14 }}
            >
              {SOURCE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={onLogIssueClick}
            sx={{
              backgroundColor: "#4D6BF6",
              borderRadius: "12px",
              textTransform: "none",
              fontSize: 14,
            }}
          >
            Log Issue
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 224 }}>
          <CircularProgress size={32} />
        </div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: "center", color: "#757575", padding: 48, minHeight: 224 }}>
          No issues found
        </div>
      ) : isMobile ? (
        <div style={{ marginTop: 16 }}>
          <ProjectCardIssues list={list} onIssueClick={onIssueClick} />
        </div>
      ) : (
        <ProjectListIssues list={list} onIssueClick={onIssueClick} hideSourceColumn={true} />
      )}

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
          <Button
            size="small"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            sx={{ textTransform: "none" }}
          >
            Previous
          </Button>
          <span style={{ fontSize: 14, color: "#6b7280" }}>
            Page {page + 1} of {totalPages}
          </span>
          <Button
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            sx={{ textTransform: "none" }}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
